import type { StatisticsSummaryView } from "../api/generated/models/StatisticsSummaryView";

type AmountByCurrency = Record<string, number>;

// ─── Safe accessors (API returns objects; generated type may be Array<any> | null) ───

/** Get conversion as object. */
export function getConversion(
  data: StatisticsSummaryView | null,
): {
  conversionPercent?: number;
  orderCount?: number;
  visitorCount?: number;
} | null {
  if (!data?.conversion) return null;
  return Array.isArray(data.conversion)
    ? (data.conversion[0] ?? null)
    : (data.conversion as any);
}

/** Get averageOrderValue as object. */
export function getAverageOrderValue(data: StatisticsSummaryView | null): {
  averageByCurrency?: AmountByCurrency;
  orderCount?: number;
  previousPeriodAverageByCurrency?: AmountByCurrency;
} | null {
  if (!data?.averageOrderValue) return null;
  return Array.isArray(data.averageOrderValue)
    ? (data.averageOrderValue[0] ?? null)
    : (data.averageOrderValue as any);
}

/** Get abandonedCarts as object (breaking change: was number, now object). */
export function getAbandonedCarts(data: StatisticsSummaryView | null): {
  abandonedCartsCount?: number;
  totalCartsCount?: number;
  confirmedCartsCount?: number;
  abandonedCartsPercent?: number;
} | null {
  if (!data?.abandonedCarts) return null;
  return Array.isArray(data.abandonedCarts)
    ? (data.abandonedCarts[0] ?? null)
    : (data.abandonedCarts as any);
}

/** Get previousPeriodComparison as object. */
export function getPreviousPeriodComparison(
  data: StatisticsSummaryView | null,
): {
  currentPeriod?: {
    totalOrders?: number;
    totalRevenueByCurrency?: AmountByCurrency;
    totalVisitors?: number;
  };
  previousPeriod?: {
    totalOrders?: number;
    totalRevenueByCurrency?: AmountByCurrency;
    totalVisitors?: number;
  };
  changes?: {
    ordersChangePercent?: number;
    revenueChangePercentByCurrency?: AmountByCurrency;
    visitorsChangePercent?: number;
  };
} | null {
  if (!data?.previousPeriodComparison) return null;
  return Array.isArray(data.previousPeriodComparison)
    ? (data.previousPeriodComparison[0] ?? null)
    : (data.previousPeriodComparison as any);
}

// ─── Currency helpers ───

/**
 * Get first available currency from amountByCurrency / averageByCurrency for display.
 */
export function getDisplayCurrency(data: StatisticsSummaryView | null): string {
  if (!data) return "EUR";
  const fromPayment = (data.salesByPaymentMethod?.[0] as any)?.amountByCurrency;
  if (
    fromPayment &&
    typeof fromPayment === "object" &&
    !Array.isArray(fromPayment) &&
    Object.keys(fromPayment).length > 0
  )
    return Object.keys(fromPayment)[0];
  const avg = getAverageOrderValue(data);
  const fromAvg = avg?.averageByCurrency;
  if (fromAvg && Object.keys(fromAvg).length > 0)
    return Object.keys(fromAvg)[0];
  const fromDay = (data.salesByDay?.[0] as any)?.amountByCurrency;
  if (
    fromDay &&
    typeof fromDay === "object" &&
    !Array.isArray(fromDay) &&
    Object.keys(fromDay).length > 0
  )
    return Object.keys(fromDay)[0];
  return "EUR";
}

/**
 * Safely extract amount for currency.
 * Handles PHP quirk: amountByCurrency can be [] (empty array) when no orders.
 */
export function getAmountForCurrency(
  amountByCurrency: AmountByCurrency | any[] | undefined,
  currency: string,
): number {
  if (!amountByCurrency || Array.isArray(amountByCurrency) || !currency)
    return 0;
  return amountByCurrency[currency] ?? 0;
}

export function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// ─── Merge helpers ───

function mergeAmountByCurrency(
  a: AmountByCurrency,
  b: AmountByCurrency | any[],
): AmountByCurrency {
  const result = { ...a };
  if (Array.isArray(b)) return result; // PHP empty array - skip
  Object.entries(b).forEach(([curr, val]) => {
    result[curr] = (result[curr] ?? 0) + val;
  });
  return result;
}

/** Normalize PHP amountByCurrency: if it's an empty array [], return {}. */
function normalizeAmountByCurrency(
  v: AmountByCurrency | any[] | undefined,
): AmountByCurrency {
  if (!v || Array.isArray(v)) return {};
  return { ...v };
}

// ─── Aggregate multiple summary responses (e.g. for "All stores") ───

export function aggregateSummaries(
  responses: StatisticsSummaryView[],
  currency: string,
): StatisticsSummaryView {
  const mergePayment = (acc: Map<string, any>, r: StatisticsSummaryView) => {
    (r.salesByPaymentMethod ?? []).forEach((item: any) => {
      const key = String(item.paymentMethodId);
      const existing = acc.get(key);
      if (existing) {
        existing.orderCount += item.orderCount ?? 0;
        existing.amountByCurrency = mergeAmountByCurrency(
          existing.amountByCurrency,
          item.amountByCurrency ?? {},
        );
      } else {
        acc.set(key, {
          ...item,
          amountByCurrency: normalizeAmountByCurrency(item.amountByCurrency),
        });
      }
    });
    return acc;
  };

  const mergeCustomerGroup = (
    acc: Map<string, any>,
    r: StatisticsSummaryView,
  ) => {
    (r.salesByCustomerGroup ?? []).forEach((item: any) => {
      const key = String(item.customerGroupId);
      const existing = acc.get(key);
      if (existing) {
        existing.orderCount += item.orderCount ?? 0;
        existing.amountByCurrency = mergeAmountByCurrency(
          existing.amountByCurrency,
          item.amountByCurrency ?? {},
        );
      } else {
        acc.set(key, {
          ...item,
          amountByCurrency: normalizeAmountByCurrency(item.amountByCurrency),
        });
      }
    });
    return acc;
  };

  const mergeTopProducts = (
    acc: Map<number, any>,
    r: StatisticsSummaryView,
  ) => {
    (r.topProducts ?? []).forEach((item: any) => {
      const existing = acc.get(item.productId);
      if (existing) {
        existing.quantitySold += item.quantitySold ?? 0;
        existing.amountByCurrency = mergeAmountByCurrency(
          existing.amountByCurrency ?? {},
          item.amountByCurrency ?? {},
        );
      } else {
        acc.set(item.productId, {
          ...item,
          amountByCurrency: normalizeAmountByCurrency(item.amountByCurrency),
        });
      }
    });
    return acc;
  };

  const mergeByDate = (
    acc: Map<
      string,
      { date: string; orderCount: number; amountByCurrency: AmountByCurrency }
    >,
    items: any[],
  ) => {
    items.forEach((item: any) => {
      const existing = acc.get(item.date);
      if (existing) {
        existing.orderCount += item.orderCount ?? 0;
        existing.amountByCurrency = mergeAmountByCurrency(
          existing.amountByCurrency,
          item.amountByCurrency ?? {},
        );
      } else {
        acc.set(item.date, {
          date: item.date,
          orderCount: item.orderCount ?? 0,
          amountByCurrency: normalizeAmountByCurrency(item.amountByCurrency),
        });
      }
    });
    return acc;
  };

  const mergeVisitors = (
    acc: Map<string, { date: string; visitorCount: number }>,
    items: any[],
  ) => {
    items.forEach((item: any) => {
      const existing = acc.get(item.date);
      if (existing) {
        existing.visitorCount += item.visitorCount ?? 0;
      } else {
        acc.set(item.date, {
          date: item.date,
          visitorCount: item.visitorCount ?? 0,
        });
      }
    });
    return acc;
  };

  // Aggregate salesByCustomerGroupByDay
  const mergeCustomerGroupByDay = (
    acc: Map<string, any>,
    r: StatisticsSummaryView,
  ) => {
    (r.salesByCustomerGroupByDay ?? []).forEach((dayItem: any) => {
      const existing = acc.get(dayItem.date);
      if (existing) {
        (dayItem.groups ?? []).forEach((g: any) => {
          const existingGroup = existing.groups.find(
            (eg: any) => eg.customerGroupId === g.customerGroupId,
          );
          if (existingGroup) {
            existingGroup.orderCount += g.orderCount ?? 0;
            existingGroup.amountByCurrency = mergeAmountByCurrency(
              existingGroup.amountByCurrency,
              g.amountByCurrency ?? {},
            );
          } else {
            existing.groups.push({
              ...g,
              amountByCurrency: normalizeAmountByCurrency(g.amountByCurrency),
            });
          }
        });
      } else {
        acc.set(dayItem.date, {
          date: dayItem.date,
          groups: (dayItem.groups ?? []).map((g: any) => ({
            ...g,
            amountByCurrency: normalizeAmountByCurrency(g.amountByCurrency),
          })),
        });
      }
    });
    return acc;
  };

  // Aggregate salesByHour
  const mergeSalesByHour = (
    acc: Map<number, any>,
    r: StatisticsSummaryView,
  ) => {
    (r.salesByHour ?? []).forEach((item: any) => {
      const existing = acc.get(item.hour);
      if (existing) {
        existing.orderCount += item.orderCount ?? 0;
        existing.visitorCount += item.visitorCount ?? 0;
        existing.amountByCurrency = mergeAmountByCurrency(
          existing.amountByCurrency,
          item.amountByCurrency ?? {},
        );
      } else {
        acc.set(item.hour, {
          ...item,
          amountByCurrency: normalizeAmountByCurrency(item.amountByCurrency),
        });
      }
    });
    return acc;
  };

  const paymentMap = responses.reduce(mergePayment, new Map());
  const customerMap = responses.reduce(mergeCustomerGroup, new Map());
  const topMap = responses.reduce(mergeTopProducts, new Map());
  const byDayMap = responses.reduce(
    (acc, r) => mergeByDate(acc, r.salesByDay ?? []),
    new Map<string, any>(),
  );
  const byMonthMap = responses.reduce(
    (acc, r) => mergeByDate(acc, r.salesByMonth ?? []),
    new Map<string, any>(),
  );
  const visitorsByDayMap = responses.reduce(
    (acc, r) => mergeVisitors(acc, r.visitorsByDay ?? []),
    new Map<string, any>(),
  );
  const visitorsByMonthMap = responses.reduce(
    (acc, r) => mergeVisitors(acc, r.visitorsByMonth ?? []),
    new Map<string, any>(),
  );
  const customerGroupByDayMap = responses.reduce(
    mergeCustomerGroupByDay,
    new Map(),
  );
  const salesByHourMap = responses.reduce(mergeSalesByHour, new Map());

  let orderCount = 0;
  let visitorCount = 0;
  let conversionSum = 0;
  let conversionN = 0;
  let totalAvg = 0;
  let avgN = 0;
  let prevAvg = 0;
  let prevAvgN = 0;
  let abandonedCartsCount = 0;
  let totalCartsCount = 0;
  let confirmedCartsCount = 0;
  // previousPeriodComparison aggregation
  let ppCurrentOrders = 0;
  let ppCurrentVisitors = 0;
  const ppCurrentRevenue: AmountByCurrency = {};
  let ppPreviousOrders = 0;
  let ppPreviousVisitors = 0;
  const ppPreviousRevenue: AmountByCurrency = {};

  responses.forEach((r) => {
    const conv = getConversion(r);
    orderCount += conv?.orderCount ?? 0;
    visitorCount += conv?.visitorCount ?? 0;
    if (conv?.conversionPercent != null) {
      conversionSum += conv.conversionPercent;
      conversionN += 1;
    }

    const avgVal = getAverageOrderValue(r);
    if (avgVal?.averageByCurrency?.[currency] != null) {
      totalAvg += avgVal.averageByCurrency[currency] * (avgVal.orderCount ?? 0);
      avgN += avgVal.orderCount ?? 0;
    }
    if (avgVal?.previousPeriodAverageByCurrency?.[currency] != null) {
      prevAvg +=
        avgVal.previousPeriodAverageByCurrency[currency] *
        (avgVal.orderCount ?? 0);
      prevAvgN += avgVal.orderCount ?? 0;
    }

    const ac = getAbandonedCarts(r);
    abandonedCartsCount += ac?.abandonedCartsCount ?? 0;
    totalCartsCount += ac?.totalCartsCount ?? 0;
    confirmedCartsCount += ac?.confirmedCartsCount ?? 0;

    const pp = getPreviousPeriodComparison(r);
    if (pp) {
      ppCurrentOrders += pp.currentPeriod?.totalOrders ?? 0;
      ppCurrentVisitors += pp.currentPeriod?.totalVisitors ?? 0;
      Object.entries(pp.currentPeriod?.totalRevenueByCurrency ?? {}).forEach(
        ([c, v]) => {
          ppCurrentRevenue[c] = (ppCurrentRevenue[c] ?? 0) + v;
        },
      );
      ppPreviousOrders += pp.previousPeriod?.totalOrders ?? 0;
      ppPreviousVisitors += pp.previousPeriod?.totalVisitors ?? 0;
      Object.entries(pp.previousPeriod?.totalRevenueByCurrency ?? {}).forEach(
        ([c, v]) => {
          ppPreviousRevenue[c] = (ppPreviousRevenue[c] ?? 0) + v;
        },
      );
    }
  });

  // Compute aggregated change percents
  const calcChange = (curr: number, prev: number): number =>
    prev > 0 ? ((curr - prev) / prev) * 100 : curr > 0 ? 100 : 0;
  const revenueChangePct: AmountByCurrency = {};
  Object.keys(ppCurrentRevenue).forEach((c) => {
    revenueChangePct[c] = calcChange(
      ppCurrentRevenue[c] ?? 0,
      ppPreviousRevenue[c] ?? 0,
    );
  });

  const result: StatisticsSummaryView = {
    salesByPaymentMethod: Array.from(paymentMap.values()),
    salesByCustomerGroup: Array.from(customerMap.values()),
    salesByCustomerGroupByDay: Array.from(customerGroupByDayMap.values()).sort(
      (a, b) => a.date.localeCompare(b.date),
    ),
    topProducts: Array.from(topMap.values()).sort(
      (a: any, b: any) => (b.quantitySold ?? 0) - (a.quantitySold ?? 0),
    ),
    conversion: {
      orderCount,
      visitorCount,
      conversionPercent: conversionN > 0 ? conversionSum / conversionN : 0,
    },
    averageOrderValue: {
      orderCount: avgN,
      averageByCurrency: { [currency]: avgN > 0 ? totalAvg / avgN : 0 },
      previousPeriodAverageByCurrency: {
        [currency]: prevAvgN > 0 ? prevAvg / prevAvgN : 0,
      },
    },
    salesByDay: Array.from(byDayMap.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    ),
    salesByMonth: Array.from(byMonthMap.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    ),
    abandonedCarts: {
      abandonedCartsCount,
      totalCartsCount,
      confirmedCartsCount,
      abandonedCartsPercent:
        totalCartsCount > 0 ? (abandonedCartsCount / totalCartsCount) * 100 : 0,
    },
    visitorsByDay: Array.from(visitorsByDayMap.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    ),
    visitorsByMonth: Array.from(visitorsByMonthMap.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    ),
    salesByHour: Array.from(salesByHourMap.values()).sort(
      (a, b) => a.hour - b.hour,
    ),
    previousPeriodComparison: {
      currentPeriod: {
        totalOrders: ppCurrentOrders,
        totalRevenueByCurrency: ppCurrentRevenue,
        totalVisitors: ppCurrentVisitors,
      },
      previousPeriod: {
        totalOrders: ppPreviousOrders,
        totalRevenueByCurrency: ppPreviousRevenue,
        totalVisitors: ppPreviousVisitors,
      },
      changes: {
        ordersChangePercent: calcChange(ppCurrentOrders, ppPreviousOrders),
        revenueChangePercentByCurrency: revenueChangePct,
        visitorsChangePercent: calcChange(
          ppCurrentVisitors,
          ppPreviousVisitors,
        ),
      },
    },
  };
  return result;
}
